import {
  ContactMoldKind,
  ContactMoldOverviewStrength,
  ContactMoldStrength,
  MoldConcentration,
} from '@ivario-arc/shared/src/enums.ts';
import { ParameterDto, RegulationDto, SampleFormat } from '@src/types/types';

export function getNumericValue(parameter: ParameterDto): number {
  const { value } = parameter;
  let numericValue: number;
  if (value.startsWith('>')) {
    numericValue = parseFloat(value.slice(1));
  } else if (value.startsWith('<')) {
    numericValue = 0;
  } else {
    numericValue = parseFloat(value);
  }

  return isNaN(numericValue) ? 0 : numericValue;
}

export function getMoldConcentrationFromRegulation(
  regulation: RegulationDto,
): MoldConcentration {
  if (!regulation.lowerLimit) {
    return MoldConcentration.LOW_MOLD;
  }
  if (!regulation.limit) {
    return MoldConcentration.HIGH_MOLD;
  }
  return MoldConcentration.MEDIUM_MOLD;
}

// The lab delivers the contact mold overview value in several free-text
// variants (e.g. "gering", "hoch, andere Partikel", "Schipi neg.") instead of
// the canonical niedrig/mittel/hoch. Normalize them to a known strength, or
// null when no mold strength is present (e.g. "Schipi neg.", "andere Partikel").
export function getContactMoldOverviewStrength(
  value: string,
): ContactMoldOverviewStrength | null {
  const normalized = value.toLowerCase();
  if (normalized.includes('hoch')) {
    return ContactMoldOverviewStrength.HIGH;
  }
  if (normalized.includes('mittel')) {
    return ContactMoldOverviewStrength.MEDIUM;
  }
  if (normalized.includes('gering') || normalized.includes('niedrig')) {
    return ContactMoldOverviewStrength.LOW;
  }
  return null;
}

export function getContactMoldOverviewConcentration(
  parameter: ParameterDto,
): MoldConcentration {
  switch (getContactMoldOverviewStrength(parameter.value)) {
    case ContactMoldOverviewStrength.LOW:
      return MoldConcentration.LOW_MOLD;
    case ContactMoldOverviewStrength.MEDIUM:
      return MoldConcentration.MEDIUM_MOLD;
    case ContactMoldOverviewStrength.HIGH:
      return MoldConcentration.HIGH_MOLD;
    default:
      return MoldConcentration.NO_MOLD;
  }
}

export function getContactMoldStrengthFromValue(
  value: string,
): ContactMoldStrength | null {
  if (value === '+' || value.includes('+')) {
    if (value.includes('++++')) {
      return ContactMoldStrength.PLUS_PLUS_PLUS_PLUS;
    } else if (value.includes('+++')) {
      return ContactMoldStrength.PLUS_PLUS_PLUS;
    } else if (value.includes('++')) {
      return ContactMoldStrength.PLUS_PLUS;
    } else {
      return ContactMoldStrength.PLUS;
    }
  }

  return null;
}

export function getContactMoldKindFromValue(value: string): ContactMoldKind {
  if (value.startsWith('V')) {
    return ContactMoldKind.ANFLUG;
  } else if (value.startsWith('B')) {
    return ContactMoldKind.BEFALL;
  }
  return ContactMoldKind.NONE;
}

// Mappings for sorting parameters
export const CONTACT_MOLD_KIND_PRIORITY = {
  [ContactMoldKind.BEFALL]: 2,
  [ContactMoldKind.ANFLUG]: 1,
  [ContactMoldKind.NONE]: 0,
};

export const MOLD_CONCENTRATION_PRIORITY = {
  [MoldConcentration.HIGH_MOLD]: 3,
  [MoldConcentration.MEDIUM_MOLD]: 2,
  [MoldConcentration.LOW_MOLD]: 1,
  [MoldConcentration.NO_MOLD]: 0,
};

export function getContactMoldConcentrationFromValue(
  value: string,
): MoldConcentration {
  // Handle special cases like "schipi neg." or null
  if (!value || value === 'schipi neg.' || value === '0') {
    return MoldConcentration.NO_MOLD;
  }

  const strength = getContactMoldStrengthFromValue(value);

  if (!strength) {
    return MoldConcentration.NO_MOLD;
  }

  switch (strength) {
    case ContactMoldStrength.PLUS:
      return MoldConcentration.LOW_MOLD;
    case ContactMoldStrength.PLUS_PLUS:
      return MoldConcentration.MEDIUM_MOLD;
    case ContactMoldStrength.PLUS_PLUS_PLUS:
    case ContactMoldStrength.PLUS_PLUS_PLUS_PLUS:
      return MoldConcentration.HIGH_MOLD;
    default:
      return MoldConcentration.NO_MOLD;
  }
}

export enum SortOption {
  CATEGORY_ASC = 'CATEGORY_ASC',
  CATEGORY_DESC = 'CATEGORY_DESC',
  ALPHABET_ASC = 'ALPHABET_ASC',
  ALPHABET_DESC = 'ALPHABET_DESC',
  DEVIATION_ASC = 'DEVIATION_ASC',
  DEVIATION_DESC = 'DEVIATION_DESC',
}

export function sortParameters(
  parameters: ParameterDto[] | undefined,
  sortOption: SortOption,
): ParameterDto[] {
  const filteredParameters = (parameters ?? []).filter(
    (parameter) => parameter.regulations.length > 0,
  );

  // Helper function to calculate the relative deviation from the limit
  const getRelativeDeviation = (parameter: ParameterDto): number => {
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      parameter.format !== SampleFormat.NUMERIC ||
      parameter.nonDetectable ||
      parameter.notAnalyzable ||
      parameter.regulations.some((reg) => reg.lowerLimit)
    ) {
      return Infinity; // Non-numeric results come last
    }

    const value =
      parameter.value.startsWith('<') || parameter.value.startsWith('>')
        ? parseFloat(parameter.value.slice(1))
        : parseFloat(parameter.value);

    if (isNaN(value)) {
      return Infinity;
    }

    // Find the lowest limit from regulations
    const lowestLimit = parameter.regulations
      .filter((reg) => reg.limit !== undefined)
      .reduce((min, reg) => Math.min(min, reg.limit ?? Infinity), Infinity);

    if (lowestLimit === 0) {
      return Infinity;
    }

    if (lowestLimit === Infinity) {
      return Infinity;
    }

    // Calculate relative deviation (value/limit)
    return value / lowestLimit;
  };

  // Helper to compare parameters by deviation
  const compareByDeviation = (
    a: ParameterDto,
    b: ParameterDto,
    descending: boolean,
  ): number => {
    const devA = getRelativeDeviation(a);
    const devB = getRelativeDeviation(b);

    // Handle non-numeric results (should be at the end, sorted alphabetically)
    if (devA === Infinity && devB === Infinity) {
      // Both have Infinity deviation, but we need to separate zero from non-zero values
      const valueA = getNumericValue(a);
      const valueB = getNumericValue(b);
      const isZeroA = valueA === 0;
      const isZeroB = valueB === 0;

      // For ascending: zero values come before non-zero values (less problematic)
      // For descending: non-zero values come before zero values (more problematic)
      const zeroComparison = descending ? 1 : -1;
      if (isZeroA && !isZeroB) return zeroComparison;
      if (!isZeroA && isZeroB) return -zeroComparison;

      // Within the same group (both zero or both non-zero), sort alphabetically
      return a.name.localeCompare(b.name);
    }
    if (devA === Infinity) return 1;
    if (devB === Infinity) return -1;

    return descending ? devB - devA : devA - devB;
  };

  // Sort parameters based on the selected sort option
  return [...filteredParameters].sort((a, b) => {
    switch (sortOption) {
      case SortOption.CATEGORY_ASC:
        // Sort by category first, then alphabetically by name
        if (a.category && b.category) {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
        } else if (a.category) {
          return -1;
        } else if (b.category) {
          return 1;
        }
        return a.name.localeCompare(b.name);

      case SortOption.CATEGORY_DESC:
        // Sort by category (desc) first, then alphabetically by name
        if (a.category && b.category) {
          if (a.category !== b.category) {
            return b.category.localeCompare(a.category);
          }
        } else if (a.category) {
          return 1;
        } else if (b.category) {
          return -1;
        }
        return a.name.localeCompare(b.name);

      case SortOption.ALPHABET_ASC:
        return a.name.localeCompare(b.name);

      case SortOption.ALPHABET_DESC:
        return b.name.localeCompare(a.name);

      case SortOption.DEVIATION_ASC: {
        return compareByDeviation(a, b, false);
      }

      case SortOption.DEVIATION_DESC: {
        return compareByDeviation(a, b, true);
      }

      default:
        return 0;
    }
  });
}
